/**
 * Customer-facing project journey. Internal `stage` keys stay unchanged
 * for installers/admins; this module maps them into labels, a 5-step
 * timeline, and a next-action condition line.
 */

export const CUSTOMER_JOURNEY = [
  { key: "proposal", label: "Proposal", desc: "Review your design" },
  { key: "paperwork", label: "Paperwork", desc: "Contract, finance or deposit" },
  { key: "survey", label: "Survey", desc: "Technical site survey" },
  { key: "installation", label: "Installation", desc: "Fit and commission" },
  { key: "live", label: "Live", desc: "Generating and in aftercare" },
] as const;

export type CustomerJourneyKey = (typeof CUSTOMER_JOURNEY)[number]["key"];

export const CUSTOMER_STAGE_COPY: Record<
  string,
  { label: string; desc: string; badgeClass: string }
> = {
  lead: {
    label: "Quote started",
    desc: "We've received your details",
    badgeClass: "bg-white/10 text-white/60",
  },
  quoted: {
    label: "Proposal ready",
    desc: "Review your design and approve when you are happy",
    badgeClass: "bg-[#3d8eff]/20 text-[#3d8eff]",
  },
  approved: {
    label: "You're confirmed",
    desc: "Next: contract, deposit or finance",
    badgeClass: "bg-[#29ffa9]/20 text-[#29ffa9]",
  },
  surveyed: {
    label: "Survey complete",
    desc: "We have the site measurements",
    badgeClass: "bg-purple-500/20 text-purple-400",
  },
  scheduled: {
    label: "Install booked",
    desc: "Your installation date is confirmed",
    badgeClass: "bg-[#3d8eff]/20 text-[#3d8eff]",
  },
  installing: {
    label: "Installing",
    desc: "The team is on site",
    badgeClass: "bg-orange-500/20 text-orange-400",
  },
  complete: {
    label: "System live",
    desc: "Installed, generating, and in aftercare",
    badgeClass: "bg-[#29ffa9]/20 text-[#29ffa9]",
  },
  aftercare: {
    label: "System live",
    desc: "Ongoing support is active",
    badgeClass: "bg-[#29ffa9]/20 text-[#29ffa9]",
  },
};

/**
 * Portal customer ↔ installer messaging. Keep in sync with
 * config('helio.portal_messaging_enabled') / PORTAL_MESSAGING_ENABLED.
 * Typed as boolean so the hidden UI is not treated as dead code.
 */
export const PORTAL_MESSAGING_ENABLED: boolean = false;

export const FINANCE_TERMINAL_OUTCOMES = ["approved", "declined", "abandoned"] as const;

export function isTerminalFinanceOutcome(outcome?: string | null): boolean {
  return FINANCE_TERMINAL_OUTCOMES.includes(outcome as (typeof FINANCE_TERMINAL_OUTCOMES)[number]);
}

export type NextActionKind =
  | "view_proposal"
  | "approve_quote"
  | "select_availability"
  | "sign_contract"
  | "pay_deposit"
  | "continue_finance"
  | "message_team"
  | "start_remote_survey"
  | "continue_remote_survey"
  | "update_remote_survey";

export type NextAction = {
  title: string;
  body: string;
  cta?: { kind: NextActionKind; label: string };
  secondaryCta?: { kind: NextActionKind; label: string };
};

/** Minimal project shape used for journey + next-action logic. */
export type ProjectProgressInput = {
  stage?: string | null;
  lifecycleStatus?: string | null;
  paymentType?: string | null;
  financeOutcome?: string | null;
  depositPaid?: boolean | null;
  depositDue?: number | null;
  surveyDate?: string | null;
  installDate?: string | null;
  customerAvailableDate?: string | null;
  contractSentAt?: string | null;
  contractSignedAt?: string | null;
  contractDeclinedAt?: string | null;
  contractExpiredAt?: string | null;
  lead?: { paymentType?: string | null } | null;
  remoteSurveyEnabled?: boolean | null;
  remoteSurveyStatus?: string | null;
  remoteSurveySubmittedAt?: string | null;
};

export function formatDepositGbp(value?: number | null): string | null {
  if (value == null || Number.isNaN(Number(value)) || Number(value) <= 0) return null;
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(Number(value));
}

export function depositCtaLabel(project: ProjectProgressInput): string {
  const formatted = formatDepositGbp(project.depositDue);
  return formatted ? `Pay ${formatted} deposit` : "Pay deposit";
}

export function formatPortalDate(iso?: string | null): string | null {
  if (!iso) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(iso)) {
    const [year, month, day] = iso.slice(0, 10).split("-").map(Number);
    const local = new Date(year, month - 1, day);
    if (Number.isNaN(local.getTime())) return null;
    return local.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export function customerStageCopy(stage?: string | null) {
  const key = stage && CUSTOMER_STAGE_COPY[stage] ? stage : "quoted";
  return CUSTOMER_STAGE_COPY[key];
}

export function projectPaymentType(project: ProjectProgressInput): string {
  return project.paymentType ?? project.lead?.paymentType ?? "upfront";
}

export function isFinanceProject(project: ProjectProgressInput): boolean {
  return projectPaymentType(project) === "finance";
}

export function isLiveStage(stage?: string | null): boolean {
  return stage === "complete" || stage === "aftercare";
}

const REMOTE_SURVEY_DONE = ["submitted", "installer_review", "complete"];

export function remoteSurveyIsComplete(project: ProjectProgressInput): boolean {
  return REMOTE_SURVEY_DONE.includes(project.remoteSurveyStatus ?? "");
}

export function paperworkReadyForSurvey(project: ProjectProgressInput): boolean {
  if (project.depositPaid) return true;
  if (isFinanceProject(project) && project.financeOutcome === "approved") return true;
  return false;
}

export function remoteSurveyLockedByStage(stage?: string | null): boolean {
  return ["scheduled", "installing", "complete", "aftercare"].includes(stage ?? "");
}

export function remoteSurveyAction(project: ProjectProgressInput): NextAction | null {
  if (project.remoteSurveyEnabled === false) return null;
  if (!paperworkReadyForSurvey(project)) return null;
  if (remoteSurveyLockedByStage(project.stage)) return null;
  const status = project.remoteSurveyStatus ?? "not_started";
  if (remoteSurveyIsComplete(project)) {
    return {
      title: "Site survey submitted",
      body: "Need to correct something? Update the survey and we'll refresh the installer pack.",
      cta: { kind: "update_remote_survey", label: "Update survey" },
    };
  }
  if (status === "in_progress" || status === "requires_customer_action") {
    return {
      title: "Continue your site survey",
      body: "Pick up where you left off. Photos and answers are saved to your account.",
      cta: { kind: "continue_remote_survey", label: "Continue survey" },
    };
  }
  return {
    title: "Complete your site survey",
    body: "A guided photo survey from your phone. Your installer gets a complete pack — no extra chasing.",
    cta: { kind: "start_remote_survey", label: "Start site survey" },
  };
}

export function contractIsSigned(project: ProjectProgressInput): boolean {
  return project.lifecycleStatus === "contract_signed" || !!project.contractSignedAt;
}

export function contractFailed(project: ProjectProgressInput): boolean {
  return (
    project.lifecycleStatus === "contract_declined" ||
    project.lifecycleStatus === "contract_expired" ||
    !!project.contractDeclinedAt ||
    !!project.contractExpiredAt
  );
}

export function contractInFlight(project: ProjectProgressInput): boolean {
  if (contractIsSigned(project) || contractFailed(project)) return false;
  return project.lifecycleStatus === "contract_sent" || !!project.contractSentAt;
}

function isPaperworkReadyToSign(project: ProjectProgressInput): boolean {
  if (contractIsSigned(project) || contractInFlight(project) || contractFailed(project)) {
    return false;
  }
  if (project.surveyDate) return false;
  if (project.depositPaid || project.lifecycleStatus === "deposit_failed") return false;
  const stage = project.stage ?? "quoted";
  const finance = isFinanceProject(project);
  const outcome = project.financeOutcome ?? null;
  if (stage === "approved" && !finance) return true;
  if (finance && outcome === "approved") return true;
  return false;
}

/** True until the agreement is sent or signed — the date is then copied into SignWell. */
export function canChangeCustomerAvailability(project: ProjectProgressInput): boolean {
  if (!project.customerAvailableDate) return false;
  if (project.installDate) return false;
  if (contractIsSigned(project) || contractInFlight(project) || contractFailed(project)) return false;
  return true;
}

export function canApproveQuote(project: ProjectProgressInput): boolean {
  return (
    (project.stage ?? "quoted") === "quoted" &&
    !contractInFlight(project) &&
    !contractIsSigned(project) &&
    !contractFailed(project)
  );
}

/**
 * 5-step customer timeline index. Unknown stages fall back to Proposal (0).
 */
export function customerJourneyIndex(project: ProjectProgressInput): number {
  const stage = project.stage ?? "quoted";
  if (isLiveStage(stage)) return 4;
  if (stage === "scheduled" || stage === "installing") return 3;
  if (
    stage === "surveyed"
    || !!project.surveyDate
    || remoteSurveyIsComplete(project)
    || paperworkReadyForSurvey(project)
  ) {
    return 2;
  }
  if (stage === "approved" || contractInFlight(project) || contractIsSigned(project) || project.depositPaid) {
    return 1;
  }
  if (stage === "quoted" || stage === "lead") return 0;
  return 0;
}

/** Customer-facing survey milestone. Remote surveys have a submit date, not a visit slot. */
export function surveyMilestone(project: ProjectProgressInput): { label: string; value: string } | null {
  const submitted = formatPortalDate(project.remoteSurveySubmittedAt);
  const booked = formatPortalDate(project.surveyDate);
  if (remoteSurveyIsComplete(project) || submitted) {
    return { label: "Survey submitted", value: submitted ?? "Complete" };
  }
  if (booked) {
    return { label: "Survey date", value: booked };
  }
  if (project.remoteSurveyEnabled !== false) {
    return null;
  }
  const journey = customerJourneyIndex(project);
  if (journey >= 1 && journey <= 2 && !formatPortalDate(project.installDate) && !isLiveStage(project.stage)) {
    return { label: "Survey date", value: "To be confirmed" };
  }
  return null;
}

export function getNextAction(project: ProjectProgressInput): NextAction {
  const stage = project.stage ?? "quoted";
  const lifecycle = project.lifecycleStatus ?? "";
  const finance = isFinanceProject(project);
  const outcome = project.financeOutcome ?? null;
  const surveyLabel = formatPortalDate(project.surveyDate);
  const installLabel = formatPortalDate(project.installDate);
  const copy = customerStageCopy(stage);

  if (isLiveStage(stage)) {
    return {
      title: "Your system is live",
      body: "Warranties, documents, and aftercare are in your portal. Refer a friend when you are ready.",
    };
  }

  if (stage === "installing") {
    return {
      title: "Installation in progress",
      body: "The team is on site fitting your system.",
    };
  }

  if (stage === "scheduled" || installLabel) {
    return {
      title: installLabel ? `Installation scheduled for ${installLabel}` : "Installation date confirmed",
      body: installLabel
        ? "We'll be in touch with arrival details closer to the day."
        : "Your installation date is being confirmed.",
    };
  }

  if (isPaperworkReadyToSign(project) && !project.customerAvailableDate) {
    return {
      title: "Choose your installation date",
      body: "Pick a day you will be available. We'll only offer dates when an installer covering your area is free, from two weeks after your proposal.",
      cta: { kind: "select_availability", label: "Choose a date" },
    };
  }

  if (isPaperworkReadyToSign(project) && project.customerAvailableDate) {
    const availableLabel = formatPortalDate(project.customerAvailableDate);
    return {
      title: "Sign your installation agreement",
      body: availableLabel
        ? `You're available on ${availableLabel}. Review and sign to confirm the design — you can change that date until you sign.`
        : "Review and sign to confirm the design, then we'll arrange the next steps.",
      cta: { kind: "sign_contract", label: "Sign agreement" },
      secondaryCta: { kind: "select_availability", label: "Change date" },
    };
  }

  const surveyCta = remoteSurveyAction(project);
  if (surveyCta) return surveyCta;

  if (stage === "surveyed") {
    return {
      title: "Survey complete",
      body: "We have the site measurements and will confirm your installation date next.",
    };
  }

  if (surveyLabel && (stage === "approved" || stage === "quoted")) {
    return {
      title: `Survey booked for ${surveyLabel}`,
      body: "A surveyor will visit to confirm the final design before installation.",
    };
  }

  if (contractFailed(project)) {
    return {
      title: "Your agreement was not completed",
      body: PORTAL_MESSAGING_ENABLED
        ? "The contract was declined or expired. Message us and we'll help you continue."
        : "The contract was declined or expired. We'll be in touch to help you continue.",
      cta: PORTAL_MESSAGING_ENABLED
        ? { kind: "message_team" as const, label: "Message the team" }
        : undefined,
    };
  }

  if (lifecycle === "deposit_failed") {
    const formatted = formatDepositGbp(project.depositDue);
    return {
      title: "Deposit payment did not go through",
      body: formatted
        ? `Nothing has been taken. Try the ${formatted} (25%) deposit again when you are ready.`
        : "Nothing has been taken. Try the deposit again when you are ready.",
      cta: { kind: "pay_deposit", label: depositCtaLabel(project) },
    };
  }

  if (finance && (outcome === "declined" || outcome === "abandoned")) {
    return {
      title: "Finance was not approved",
      body: PORTAL_MESSAGING_ENABLED
        ? "We can help with other payment options so your installation can still go ahead."
        : "We can help with other payment options so your installation can still go ahead. We'll be in touch.",
      cta: PORTAL_MESSAGING_ENABLED
        ? { kind: "message_team" as const, label: "Talk to the team" }
        : undefined,
    };
  }

  if (finance && outcome === "approved" && stage === "approved") {
    const ready = remoteSurveyAction(project);
    if (ready) return ready;
    return {
      title: "Finance approved",
      body: "Complete your remote site survey so the installer can prepare.",
      cta: { kind: "start_remote_survey", label: "Start site survey" },
    };
  }

  if (contractIsSigned(project) && !finance && !project.depositPaid) {
    const formatted = formatDepositGbp(project.depositDue);
    return {
      title: "Pay your deposit to lock the install diary",
      body: formatted
        ? `Your agreement is signed. A ${formatted} (25%) deposit reserves your survey and installation dates.`
        : "Your agreement is signed. A deposit reserves your survey and installation dates.",
      cta: { kind: "pay_deposit", label: depositCtaLabel(project) },
    };
  }

  if (contractInFlight(project)) {
    return {
      title: "Contract sent — waiting for your signature",
      body: "Sign your installation agreement to lock in the next steps.",
      cta: { kind: "sign_contract", label: "Sign agreement" },
    };
  }

  if (finance && (outcome === "clicked_pending" || (stage === "approved" && !isTerminalFinanceOutcome(outcome)))) {
    return {
      title: "Phoenix Financial Consultants is reviewing your application",
      body: "No deposit is needed — Phoenix Financial Consultants covers the system cost once approved. Continue the application if you have not finished it.",
      cta: { kind: "continue_finance", label: "Continue finance application" },
    };
  }

  if (stage === "approved" && !finance && !contractIsSigned(project) && !contractInFlight(project)) {
    return {
      title: "Sign your installation agreement",
      body: "Review and sign to confirm the design, then we'll arrange your deposit and survey.",
      cta: { kind: "sign_contract", label: "Sign agreement" },
    };
  }

  if (stage === "quoted" && finance && outcome === "clicked_pending") {
    return {
      title: "Phoenix Financial Consultants is reviewing your application",
      body: "You can still open your proposal while the lender reviews your application.",
      cta: { kind: "continue_finance", label: "Continue finance application" },
    };
  }

  if (stage === "quoted" || stage === "lead") {
    return {
      title: "Your proposal is ready to review",
      body: copy.desc,
      cta: { kind: "view_proposal", label: "View proposal" },
    };
  }

  return {
    title: copy.label,
    body: copy.desc,
  };
}
